import React, { useRef, useState } from 'react';
import { useOnClickOutside } from '../../hooks/useOnClickOutside.js';
import './MultiSelectDropdown.css';

export interface MultiSelectOption {
  value: string;
  label: string;
}

export interface MultiSelectDropdownProps {
  label: string;
  selected: string[];
  onChange: (values: string[]) => void;
  /** Called on open-toggle, not memoized against the buffer — keeps options fresh without recomputing on every render. */
  getOptions: () => MultiSelectOption[];
  placeholder?: string;
}

export const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  label,
  selected,
  onChange,
  getOptions,
  placeholder,
}) => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<MultiSelectOption[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);

  useOnClickOutside(containerRef, () => setOpen(false));

  const handleToggle = () => {
    if (!open) {
      setOptions(getOptions());
    }
    setOpen((o) => !o);
  };

  const toggleValue = (value: string) => {
    onChange(selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value]);
  };

  return (
    <div
      className="fop-msdd"
      ref={containerRef}
      onKeyDown={(e) => {
        if (e.key === 'Escape') setOpen(false);
      }}
    >
      <button
        type="button"
        className={`fop-msdd__trigger ${selected.length > 0 ? 'fop-msdd__trigger--active' : ''}`}
        onClick={handleToggle}
      >
        <span>{selected.length > 0 ? `${label} (${selected.length})` : (placeholder ?? label)}</span>
        <span className="fop-msdd__caret">{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div className="fop-msdd__popover" role="listbox" aria-label={label}>
          <div className="fop-msdd__actions">
            <button type="button" onClick={() => onChange(options.map((o) => o.value))} disabled={options.length === 0}>
              Select all
            </button>
            <button type="button" onClick={() => onChange([])} disabled={selected.length === 0}>
              Clear
            </button>
          </div>
          <div className="fop-msdd__options">
            {options.length === 0 ? (
              <div className="fop-msdd__empty">No values available</div>
            ) : (
              options.map((opt) => (
                <label key={opt.value} className="fop-msdd__option">
                  <input type="checkbox" checked={selected.includes(opt.value)} onChange={() => toggleValue(opt.value)} />
                  <span>{opt.label}</span>
                </label>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};
